import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useSite } from "../../context/SiteContext";
import { api } from "../../lib/api";
import { MODE_LABELS, dateTime, humanize, money, shortDate } from "../../lib/format";
import { DataTable, NextStage, PageHeader, StatusPill, TestBadge } from "../../components/admin/ui";
import { CheckSettingsCard, PaymentActionsDialog } from "../../components/admin/PaymentActions";
import { useAuth } from "../../context/AuthContext";
import { VerificationPayments } from "../../components/admin/VerificationPayments";

const useList = (url, pick = (d) => d) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url).then((r) => setData(pick(r.data))); }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
};

export function Payments() {
  const { payment } = useSite();
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  const [sel, setSel] = useState(null);
  const rows = useList(`/admin/payments?t=${tick}`) || [];
  const canAct = user?.role === "super_admin" || (user?.role === "editor" && user?.can_manage_payments);
  const cols = [{ key: "reference", label: "Reference", render: (r) => <span className="font-mono text-xs">{r.reference}</span> },
    { key: "target_type", label: "For", render: (r) => humanize(r.target_type || "registration") },
    { key: "method", label: "Method", render: (r) => <>{r.provider === "test" && !r.method ? "SIMULATED payment" : humanize(r.method || r.provider)} <TestBadge show={r.is_test} /></> },
    { key: "amount", label: "Amount", render: (r) => <>{money(r.amount, r.currency?.toUpperCase())}{r.amount_mismatch && <b className="ml-1 text-burgundy" title="Amount mismatch">!</b>}</> },
    { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
    { key: "ref", label: "Order ID / auth / check", render: (r) => <span className="font-mono text-xs">{r.order_id ? `${r.order_id}${r.auth_code ? ` · ${r.auth_code}` : ""}${r.card_last4 ? ` · ••${r.card_last4}` : ""}` : r.check_reference ? `${r.check_reference}${r.check_number ? ` · #${r.check_number}` : ""}` : r.provider_ref}</span> },
    { key: "date", label: "Date", render: (r) => dateTime(r.created_at) }];
  return (
    <div data-testid="admin-payments">
      <PageHeader title="Payments" subtitle={`Adapter: ${payment.configured ? `${payment.provider}${payment.is_test ? " (TEST)" : ""}` : "not configured: payment setup pending"} · Revenue counts completed payments only. Click a row for actions.`} />
      <DataTable columns={cols} rows={rows} onRowClick={setSel} testid="payments-table" empty="No payments yet." />
      <PaymentActionsDialog p={sel} canAct={canAct} onClose={() => setSel(null)} onChanged={() => setTick((t) => t + 1)} />
      {user?.role === "super_admin" && <LiveTests />}
      {user?.role === "super_admin" && <VerificationPayments />}
      <CheckSettingsCard canEdit={user?.role === "super_admin"} />
    </div>
  );
}

function LiveTests() {
  const rows = useList("/admin/integrations/gateway/live-test/records") || [];
  if (!rows.length) return null;
  const cols = [{ key: "label", label: "For" }, { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
    { key: "amount", label: "Amount", render: (r) => `${r.amount} ${r.currency?.toUpperCase()}` },
    { key: "ref", label: "Our ref / auth", render: (r) => <span className="font-mono text-xs">{r.order_id}{r.auth_code ? ` · ${r.auth_code}` : ""}{r.card_last4 ? ` · ••${r.card_last4}` : ""}</span> },
    { key: "date", label: "Date", render: (r) => dateTime(r.at) }];
  return (
    <div className="mt-6" data-testid="gateway-live-tests">
      <h3 className="mb-1 font-semibold text-navy">Gateway live tests</h3>
      <p className="mb-2 text-xs text-slate-600">Excluded from revenue, reports, SAG fees and tickets; not linked to any registration or sponsor.</p>
      <DataTable columns={cols} rows={rows} testid="gateway-live-tests-table" empty="None." />
    </div>
  );
}

export function Communications() {
  const logs = useList("/admin/communications") || [];
  const templates = useList("/admin/email-templates") || [];
  const [view, setView] = useState(null);
  const cols = [{ key: "to", label: "To", render: (r) => <>{r.to} <TestBadge show={r.is_test} /></> }, { key: "template", label: "Template", render: (r) => humanize(r.template) },
    { key: "subject", label: "Subject" }, { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> }, { key: "date", label: "Date", render: (r) => dateTime(r.created_at) }];
  return (
    <div data-testid="admin-communications">
      <PageHeader title="Email Communications" subtitle="Rows marked 'Email not sent – SMTP not configured' were logged only. A Super Admin can set up and enable SMTP under Integrations." />
      <Tabs defaultValue="log">
        <TabsList><TabsTrigger value="log" data-testid="comms-log-tab">Communication Log</TabsTrigger><TabsTrigger value="templates" data-testid="comms-templates-tab">Templates</TabsTrigger></TabsList>
        <TabsContent value="log"><DataTable columns={cols} rows={logs} onRowClick={setView} testid="comms-table" empty="No communications logged." /></TabsContent>
        <TabsContent value="templates">
          <p className="mb-3 text-sm text-slate-600">Rendered with the current event record (date/venue changes propagate automatically).</p>
          <ul className="space-y-3">{templates.map((t) => <li key={t.key} className="admin-card" data-testid={`template-${t.key}`}><p className="font-semibold text-navy">{t.subject}</p><pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-700">{t.body}</pre></li>)}</ul>
          <div className="mt-6"><NextStage title="Email delivery & bulk email" items={["EmailProvider.send(to, subject, body) -> provider id", "CommunicationLog.status: logged -> queued -> sent/failed"]} /></div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl" data-testid="comms-detail-dialog"><DialogHeader><DialogTitle>{view?.subject}</DialogTitle></DialogHeader>
          <p className="text-sm">To: {view?.to} · <StatusPill value={view?.status} /> · {view?.status_note}</p>
          <pre className="whitespace-pre-wrap font-sans text-sm">{view?.body}</pre></DialogContent>
      </Dialog>
    </div>
  );
}

const Breakdown = ({ title, rows, fmt = (x) => x }) => (
  <div className="admin-card"><h2 className="mb-2 text-lg text-navy">{title}</h2>
    <ul className="text-sm">{rows.map((r) => <li key={r._id ?? "none"} className="flex justify-between border-b border-slate-100 py-1.5"><span>{fmt(r._id) || "—"}</span><b>{r.count}</b></li>)}</ul></div>
);

export function Reports() {
  const d = useList("/admin/reports/summary");
  const messages = useList("/admin/contact-messages") || [];
  if (!d) return <p aria-busy="true">Loading…</p>;
  return (
    <div data-testid="admin-reports">
      <PageHeader title="Reports" subtitle="Basic summaries (including test data). Advanced analytics come in the next stage." />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Breakdown title="By category" rows={d.by_category} />
        <Breakdown title="By attendance" rows={d.by_attendance} fmt={(x) => MODE_LABELS[x]} />
        <Breakdown title="By registration status" rows={d.by_registration_status} fmt={humanize} />
        <Breakdown title="By payment status" rows={d.by_payment_status} fmt={humanize} />
      </div>
      <h2 className="mb-3 mt-8 text-xl text-navy">Contact messages</h2>
      <DataTable testid="contact-messages-table" rows={messages} empty="No messages." columns={[{ key: "name", label: "Name" }, { key: "email", label: "Email" },
        { key: "subject", label: "Subject" }, { key: "message", label: "Message" }, { key: "date", label: "Date", render: (r) => shortDate(r.created_at) }]} />
      <div className="mt-6"><NextStage title="Advanced analytics" items={["Registrations over time", "Revenue by category", "Conversion funnel"]} /></div>
    </div>
  );
}
